import { useQuery } from "@tanstack/react-query";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../lib/supabase";
import { queryKeys } from "../../lib/queryKeys";
import { listEmojiOrDefault } from "../../constants/defaults";
import {
  Task,
  SubTask,
  Tag,
  TaskAttachment,
  TaskLink,
  SubtaskAttachment,
  SubtaskLink,
} from "../../types";

// Raw shape returned by the PostgREST embedded-resource select.
// Supabase doesn't auto-generate types here (no DB type file), so we
// declare what the select actually returns and cast at the boundary.
interface RawTaskRow {
  id: string;
  user_id: string;
  list_id: string | null;
  title: string;
  description: string;
  priority: number;
  completed: boolean;
  archived: boolean;
  scheduled_date: string | null;
  scheduled_time: string | null;
  deadline_date: string | null;
  duration: number | null;
  persistent: boolean;
  working_hours_only?: boolean;
  recurrence_frequency: string | null;
  recurrence_interval: number;
  recurrence_day_of_week: number | null;
  recurrence_end_date: string | null;
  created_at: string;
  order_position: number;
  updated_at: string;
  // Embedded: lists row (nullable when task has no list)
  lists: { name: string; emoji: string } | null;
  // Embedded: task_tags junction rows → each carries the joined tags row
  task_tags: Array<{ tag_id: string; tags: Tag | null }>;
  task_attachments: TaskAttachment[];
  task_links: TaskLink[];
  subtasks: Array<
    Omit<SubTask, "tags" | "attachments" | "links"> & {
      subtask_tags: Array<{ tag_id: string; tags: Tag | null }>;
      subtask_attachments: SubtaskAttachment[];
      subtask_links: SubtaskLink[];
    }
  >;
}

function transformTask(raw: RawTaskRow): Task {
  const {
    lists: listData,
    task_tags,
    task_attachments,
    task_links,
    subtasks: rawSubtasks,
    ...rest
  } = raw;

  const tags: Tag[] = (task_tags ?? [])
    .map((tt) => tt.tags)
    .filter((t): t is Tag => t !== null);

  const subtasks: SubTask[] = (rawSubtasks ?? [])
    .sort((a, b) => a.order_position - b.order_position)
    .map((st) => {
      const { subtask_tags, subtask_attachments, subtask_links, ...stRest } =
        st;
      return {
        ...stRest,
        tags: (subtask_tags ?? [])
          .map((tt) => tt.tags)
          .filter((t): t is Tag => t !== null),
        attachments: subtask_attachments ?? [],
        links: subtask_links ?? [],
      };
    });

  return {
    ...rest,
    list: listData?.name ?? "",
    listEmoji: listEmojiOrDefault(listData?.emoji),
    tags,
    attachments: task_attachments ?? [],
    links: task_links ?? [],
    subtasks,
  } as Task;
}

export async function fetchTasks(): Promise<Task[]> {
  const { data, error } = await supabase
    .from("tasks")
    .select(
      `*,
      lists(name, emoji),
      task_tags(tag_id, tags(*)),
      task_attachments(*),
      task_links(*),
      subtasks(
        *,
        subtask_tags(tag_id, tags(*)),
        subtask_attachments(*),
        subtask_links(*)
      )`
    )
    .order("order_position", { ascending: true });

  if (error) throw error;

  return ((data ?? []) as unknown as RawTaskRow[])
    .map(transformTask)
    .sort((a, b) => a.order_position - b.order_position);
}

export function useTasks() {
  const { user } = useAuth();

  return useQuery({
    queryKey: queryKeys.tasks,
    queryFn: fetchTasks,
    enabled: !!user,
  });
}

